import { useRef, useState } from 'react'
import { apiUploadForm, ApiError, CancelledError } from '../api/client'
import { trackTranscriptionFailed } from '../analytics/posthog'
import { useT } from '../i18n/useT'
import {
  MAX_DURATION_SECONDS,
  buildWholeFilePlayback,
  cutIntoChunks,
  exceedsMaxDuration,
  type AudioChunk,
} from '../lib/audioChunks'
import { isWma } from '../lib/asf'
import { decidingDurationSeconds } from '../lib/mediaDuration'
import type { PlaybackAudio } from '../lib/playbackAudio'
import {
  audioDone,
  estimateRemaining,
  processingRate,
  type EtaSample,
} from '../lib/etaTracker'
import type { OutputFormat, TranscriptionResult } from '../types/transcription'

/** A part that failed for a reason a second attempt could fix. A gateway that
 * examined the file and refused it will refuse it again; a dropped connection
 * will not.
 *
 * **Status 0 is the whole point of this function.** `apiUploadForm` wraps a
 * dropped connection as `ApiError(0, …)`, so `status >= 500` excluded exactly
 * the case the retries exist for — one network blip killed an hour-long job
 * on its second part. */
export function worthRetrying(err: unknown): boolean {
  // Asked to stop is not a failure to ride out
  if (err instanceof CancelledError) return false
  if (!(err instanceof ApiError)) return true
  return err.status === 0 || err.status >= 500
}

/** Two more goes after the first. A single dropped connection must not cost
 * someone an hour of processing. */
const PART_ATTEMPTS = 3

/** Below this a file cannot be past the chunking threshold at any bitrate we
 * handle — 15 minutes at 4 kbit/s — so a slow first probe is not worth
 * asking twice about. */
const MIN_BYTES_WORTH_A_SECOND_ASK = 450_000

/** An id for the run.
 *
 * `crypto.randomUUID` exists only in a SECURE CONTEXT — https or localhost —
 * so on a plain-http host it is undefined, and reaching for it unguarded
 * would fail every upload rather than the one thing it is needed for. The
 * fallback is a version-4 uuid built from `getRandomValues`, and if even that
 * is missing, `Math.random`: the id only has to be unique enough for one
 * browser's retries to find one row. */
function runIdentifier(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c?.randomUUID) return c.randomUUID()
  const b = new Uint8Array(16)
  if (c?.getRandomValues) c.getRandomValues(b)
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface TranscriptionProgress {
  /** What is happening, as a fact rather than something inferred from the
   * percentage. The panel used to read the stage off `uploadProgress < 100`,
   * which is why it said "Uploading" through the tens of seconds the gateway
   * spends recognising — the longer half of every part. */
  stage: 'splitting' | 'sending' | 'recognising' | null
  /** 1-based; null while there is only one part */
  part: number | null
  parts: number | null
  /** Seconds of the recording already behind us — the offset of the part in
   * hand. Minutes of audio are what the person uploaded; parts are ours. */
  secondsDone: number | null
  /** Seconds in the whole recording */
  secondsTotal: number | null
  /** Seconds still to wait; null until there is enough measured to say */
  etaSeconds: number | null
}

interface UseTranscriptionReturn {
  isLoading: boolean
  /** For a recording that was cut up: audio the player can seek. Null for
   * everything else, and the player keeps the file the user chose. */
  playbackAudio: PlaybackAudio | null
  /** 0-100 across the whole file, however many parts it takes; null otherwise */
  uploadProgress: number | null
  progress: TranscriptionProgress
  result: TranscriptionResult | null
  error: string | null
  transcribe: (file: File, language: string, outputFormat: OutputFormat) => Promise<TranscriptionResult | null>
  /** Stop a run in flight. An hour-long recording is minutes of waiting with
   * no way out otherwise — and the ✕ on the file only cleared the form while
   * the parts kept going up behind it. */
  cancel: () => void
  reset: () => void
}

const IDLE: TranscriptionProgress = {
  stage: null,
  part: null,
  parts: null,
  secondsDone: null,
  secondsTotal: null,
  etaSeconds: null,
}

/** How often the estimate and the bar are recomputed. Half a second is well
 * under the granularity of anything shown (the bar moves a pixel or two, the
 * estimate is whole minutes) and costs nothing — the point is that neither
 * ever sits still for the length of a part. */
const TICK_MS = 500

/** What the ticker needs to know about the run in flight. A ref, not state:
 * it is written from inside the upload loop and read from a timer, and
 * neither should re-render anything by itself. */
interface Run {
  partCount: number
  partIndex: number
  /** When the part in hand started, `Date.now()` */
  partStartedAt: number
  /** Seconds into the recording where each part begins, plus the total as a
   * final entry — so part i covers [offsets[i], offsets[i + 1]) */
  offsets: number[]
  audioTotal: number
  samples: EtaSample[]
  stage: 'sending' | 'recognising'
  /** The furthest along the recording we have claimed to be, in seconds. A
   * retried part restarts its clock — correctly, since the time it spent
   * bought nothing — but a bar that walks backwards says the work was
   * undone, and it wasn't. */
  floor: number
}

export function useTranscription(onLimitError?: () => void): UseTranscriptionReturn {
  const t = useT()
  const [isLoading, setIsLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [progress, setProgress] = useState<TranscriptionProgress>(IDLE)
  const [result, setResult] = useState<TranscriptionResult | null>(null)
  const [playbackAudio, setPlaybackAudio] = useState<PlaybackAudio | null>(null)
  const [error, setError] = useState<string | null>(null)
  const runRef = useRef<Run | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // The run whose result is on screen. Work that finishes after its run did
  // — the WMA player copy — checks this first: a reset or a newer run means
  // there is nothing left for it to belong to.
  const shownRunRef = useRef<string | null>(null)

  function cancel() {
    abortRef.current?.abort()
  }

  /** Move the bar and the estimate between parts, so neither stands still
   * through the recognition — which is the longer half of every part and the
   * whole reason the screen looked frozen. */
  function tick() {
    const run = runRef.current
    if (!run) return
    const { partIndex, partCount, offsets, audioTotal, samples } = run
    const elapsed = (Date.now() - run.partStartedAt) / 1000
    const partAudio = (offsets[partIndex + 1] ?? audioTotal) - offsets[partIndex]
    const expected = partAudio / processingRate(samples)
    // ONE number behind both the fill and the figure under it. They used to
    // be computed apart — the fill from the part count, the figure from the
    // true offset — so they disagreed all the way through and, with the
    // figure now sitting directly beneath the fill's own edge, disagreed in
    // one place on the screen.
    run.floor = Math.max(run.floor, audioDone(offsets[partIndex], partAudio, elapsed, expected))
    setUploadProgress(audioTotal > 0 ? Math.round((run.floor / audioTotal) * 100) : 0)
    setProgress({
      stage: run.stage,
      part: partIndex + 1,
      parts: partCount,
      secondsDone: run.floor,
      secondsTotal: audioTotal,
      // Not from the floor: this one is exact arithmetic on where the part
      // really starts, and there is no reason to feed it a guess
      etaSeconds: estimateRemaining(samples, audioTotal - offsets[partIndex], elapsed),
    })
  }

  /** One request: the whole file, or one part of it. */
  async function sendPart(
    chunk: AudioChunk,
    language: string,
    outputFormat: OutputFormat,
    part: { index: number; count: number; jobId: string },
    onUpload: (percent: number) => void,
    signal: AbortSignal,
  ): Promise<TranscriptionResult> {
    const formData = new FormData()
    formData.append('file', chunk.file)
    formData.append('language', language)
    formData.append('output_format', outputFormat)
    // Sent for a single upload too, and that is the point: a response lost on
    // the way back looks exactly like a request that never arrived, so the
    // retry re-sends the file. Without an id of ours to recognise it by, the
    // server recognised the audio a second time and charged for it — three
    // attempts, three charges. With one, the repeat gets what is stored.
    formData.append('job_id', part.jobId)
    if (part.count > 1) {
      formData.append('part_index', String(part.index))
      formData.append('part_count', String(part.count))
      formData.append('part_offset', String(chunk.offset))
    }
    return apiUploadForm<TranscriptionResult>('/transcriptions', formData, onUpload, signal)
  }

  async function transcribe(
    file: File,
    language: string,
    outputFormat: OutputFormat,
  ): Promise<TranscriptionResult | null> {
    setIsLoading(true)
    setError(null)
    setResult(null)
    setPlaybackAudio(null)
    shownRunRef.current = null
    setUploadProgress(0)
    const controller = new AbortController()
    abortRef.current = controller
    // One id for the whole run — see sendPart for why the server needs it
    const runId = runIdentifier()
    setProgress(IDLE)

    // Which part was in hand when it went wrong — the error is far more use
    // naming it than saying "transcription failed" about an eight-hour file
    let failedAt: { part: number; total: number } | null = null
    let ticker = 0
    let recognising = false

    try {
      // Over ~15 minutes the gateway cannot finish inside one request, so the
      // recording is cut on a pause here in the browser and sent part by
      // part. Under the threshold nothing happens: same single request, same
      // bytes, and ffmpeg is never even loaded.
      // Not the analytics probe: this answer decides whether the recording is
      // cut, and a null sends an eight-hour file as one request
      const duration = await decidingDurationSeconds(
        file,
        MIN_BYTES_WORTH_A_SECOND_ASK,
        controller.signal,
      )
      // The first place a cancel used to be noticed was inside cutIntoChunks,
      // which is never called when the duration came back null — so a Cancel
      // pressed during the probe above stopped the upload silently and left
      // the spinner turning for the rest of the wait
      if (controller.signal.aborted) throw new CancelledError()
      // Nothing over the ceiling is cut up or sent — the guarantee lives
      // here, on the one path every upload goes through
      if (exceedsMaxDuration(duration)) {
        setError(t('err.tooLong', { hours: MAX_DURATION_SECONDS / 3600 }))
        return null
      }
      const { chunks, playback } = await cutIntoChunks(file, duration, (p) => {
        // Cutting an eight-hour file takes minutes of its own
        if (controller.signal.aborted) throw new CancelledError()
        setProgress({ ...IDLE, stage: 'splitting', parts: p.phase === 'cut' ? p.total : null })
      })
      const total = chunks.length
      const chunked = total > 1
      // No browser can play a WMA, and one short enough to go whole never
      // passed through the cut that gives a long one its MP3 copy. So a copy
      // is encoded here, BESIDE the upload rather than before it: seconds of
      // work against the tens the gateway spends recognising, none of them on
      // the way to the result. The run's signal reaches it, so Cancel stops
      // the encoder too.
      const wholeFile = !chunked && isWma(file) ? buildWholeFilePlayback(file, duration, controller.signal) : null

      if (chunked) {
        runRef.current = {
          partCount: total,
          partIndex: 0,
          partStartedAt: Date.now(),
          // Part i covers [offsets[i], offsets[i + 1]); the total closes the
          // last one, which is routinely a third the length of the others
          offsets: [...chunks.map((c) => c.offset), duration as number],
          audioTotal: duration as number,
          samples: [],
          stage: 'sending',
          floor: 0,
        }
        ticker = window.setInterval(tick, TICK_MS)
        tick()
      } else {
        setProgress({ ...IDLE, stage: 'sending' })
      }

      // Ours from the start now, rather than learned from the first reply
      const jobId = runId
      let last: TranscriptionResult | null = null
      for (let i = 0; i < total; i++) {
        if (controller.signal.aborted) throw new CancelledError()
        const run = runRef.current
        if (run) {
          run.partIndex = i
          run.partStartedAt = Date.now()
          run.stage = 'sending'
          tick()
        }
        // A single upload keeps the bar it always had: bytes over the wire,
        // which is the whole of the wait for a file small enough not to be
        // cut. For a chunked one the bar is driven by the clock instead —
        // the upload of a part is seconds against a minute of recognition,
        // so measuring the bytes would leave it frozen most of the time.
        const onUpload = chunked
          ? (percent: number) => {
              const r = runRef.current
              if (r && percent >= 100 && r.stage === 'sending') {
                r.stage = 'recognising'
                tick()
              }
            }
          : (percent: number) => {
              setUploadProgress(Math.round(percent))
              // The bytes are up; what is left is the gateway thinking
              if (percent >= 100 && !recognising) {
                recognising = true
                setProgress({ ...IDLE, stage: 'recognising' })
              }
            }

        let attempt = 0
        for (;;) {
          try {
            last = await sendPart(
              chunks[i],
              language,
              outputFormat,
              { index: i, count: total, jobId },
              onUpload,
              controller.signal,
            )
            break
          } catch (err) {
            attempt++
            if (attempt >= PART_ATTEMPTS || !worthRetrying(err)) {
              if (chunked) failedAt = { part: i + 1, total }
              throw err
            }
            // A retried part starts its clock again: the time already spent
            // was spent on a request that produced nothing
            if (runRef.current) runRef.current.partStartedAt = Date.now()
          }
        }
        if (run) {
          run.samples.push({
            audioSeconds: run.offsets[i + 1] - run.offsets[i],
            elapsedSeconds: (Date.now() - run.partStartedAt) / 1000,
          })
        }
      }

      setResult(last)
      // Only once the transcription is in hand: a rendition kept after a
      // failed upload is megabytes nothing will ever play
      setPlaybackAudio(playback)
      shownRunRef.current = runId
      // Usually ready long before the result is; when it is not, the
      // player's tab turns on the moment it lands — unless a reset or a newer
      // run has taken the screen in the meantime
      void wholeFile?.then((audio) => {
        if (audio && shownRunRef.current === runId) setPlaybackAudio(audio)
      })
      return last
    } catch (err) {
      // Asked to stop: not a failure, so no message and nothing tracked. The
      // form keeps the file, because the usual next move is to try again.
      if (err instanceof CancelledError) return null

      // Analytics keeps the technical string; the user gets a sentence.
      trackTranscriptionFailed(err instanceof Error ? err.message : 'unknown')

      // 403 = out of credits server-side (e.g. the anonymous per-IP cap that
      // a cleared localStorage can't see) — the limit dialog is the message
      if (err instanceof ApiError && err.status === 403 && onLimitError) {
        onLimitError()
        return null
      }

      // Only the API's own `detail` is worth quoting ("No speech detected in
      // audio"). A 502 from Cloudflare carries an HTML page instead, and
      // `statusText` is empty on HTTP/2 — that combination used to reach the
      // screen as an empty string, which renders as nothing at all: the
      // upload failed and the form looked untouched.
      const detail = err instanceof ApiError ? err.detail : null
      // A connection that never produced a response has no server sentence to
      // quote, and something of its own to say. The cause is not knowable
      // from here — wifi, a sleeping laptop, a proxy cutting a long POST —
      // so the message names none of it as usual; it just gives the advice
      // that fits any of them, which "Transcription failed" does not.
      const dropped = err instanceof ApiError && err.status === 0
      if (dropped) {
        setError(t('err.network'))
      } else if (failedAt) {
        // The server's own `detail` here is the part's error, not the file's
        // — and on a timeout it is Cloudflare's HTML anyway. Saying which
        // part stopped, and that nothing was charged, is the useful answer.
        setError(t('err.partFailed', failedAt))
      } else {
        setError(detail ?? t('err.transcriptionFailed'))
      }
      return null
    } finally {
      if (ticker) window.clearInterval(ticker)
      runRef.current = null
      abortRef.current = null
      // A run that ends with nothing on screen — failed, refused, cancelled —
      // stops what it started beside the upload instead of letting it finish
      // for no one
      if (shownRunRef.current !== runId) controller.abort()
      setIsLoading(false)
      setUploadProgress(null)
      setProgress(IDLE)
    }
  }

  function reset() {
    shownRunRef.current = null
    setResult(null)
    setPlaybackAudio(null)
    setError(null)
  }

  return { isLoading, uploadProgress, progress, result, playbackAudio, error, transcribe, cancel, reset }
}
